import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import * as matchesApi from "@api/matches.api";
import * as matchEventsApi from "@api/match-events.api";
import { TeamId } from "@/constants/teams";
import type { Match, MatchEvent, MatchPlayer, Player } from "@/types";

type MatchWithRoster = Match & {
  matchPlayers: (MatchPlayer & { player: Player })[];
};

export function useLiveMatch() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { notify } = useNotification();

  const [match, setMatch] = useState<MatchWithRoster | null>(null);
  const [events, setEvents] = useState<MatchEvent[]>([]);
  const [score, setScore] = useState({ red: 0, blue: 0 });
  const [loading, setLoading] = useState(true);

  const [goalDialog, setGoalDialog] = useState<{ teamId: TeamId } | null>(null);
  const [selectedScorer, setSelectedScorer] = useState<string | null>(null);
  const [selectedAssister, setSelectedAssister] = useState<string | null>(null);
  const [isOwnGoal, setIsOwnGoal] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      const [m, evts, sc] = await Promise.all([
        matchesApi.getMatch(id),
        matchEventsApi.getMatchEvents(id),
        matchEventsApi.getMatchScore(id),
      ]);
      setMatch(m as MatchWithRoster);
      setEvents(evts);
      setScore(sc);
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const teamPlayers = useCallback(
    (teamId: TeamId) =>
      match?.matchPlayers?.filter((mp) => mp.teamId === teamId) ?? [],
    [match],
  );

  const resetGoalForm = useCallback(() => {
    setGoalDialog(null);
    setSelectedScorer(null);
    setSelectedAssister(null);
    setIsOwnGoal(false);
  }, []);

  const handleRecordGoal = async () => {
    if (!goalDialog || !selectedScorer || !id) return;
    try {
      await matchEventsApi.createMatchEvent(id, {
        scorerId: selectedScorer,
        assisterId: selectedAssister ?? undefined,
        teamId: goalDialog.teamId,
        isOwnGoal,
      });
      resetGoalForm();
      await loadData();
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!id) return;
    try {
      await matchEventsApi.deleteMatchEvent(id, eventId);
      await loadData();
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  const handleEndMatch = async () => {
    if (!id) return;
    try {
      await matchesApi.endMatch(id);
      notify(t("match.ended"), "success");
      navigate("/history");
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
    setEndOpen(false);
  };

  return {
    id,
    match,
    events,
    score,
    loading,
    goalDialog,
    setGoalDialog,
    selectedScorer,
    setSelectedScorer,
    selectedAssister,
    setSelectedAssister,
    isOwnGoal,
    setIsOwnGoal,
    endOpen,
    setEndOpen,
    teamPlayers,
    resetGoalForm,
    handleRecordGoal,
    handleDeleteEvent,
    handleEndMatch,
  };
}
